import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { bootstrapUser, updateBusinessProfile } from '../services/api';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const DAYS_ES = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const defaultHours = () => DAYS_ES.reduce((acc, d) => ({ ...acc, [d]: { closed: false, open: '09:00', close: '23:00' } }), {});

const card = { background: '#121A17', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 24, marginBottom: 16, maxWidth: 560 };
const label = { fontSize: 12, color: '#9CA8A2', marginBottom: 6, display: 'block', fontFamily: "'Nunito', sans-serif" };
const input = { width: '100%', background: '#0A0F0D', border: '1.5px solid rgba(255,255,255,0.08)', borderRadius: 10, padding: '10px 12px', color: '#F4F7F5', fontSize: 14, marginBottom: 14, boxSizing: 'border-box', fontFamily: "'Nunito', sans-serif" };
const btnPrimary = { background: '#2EE06B', color: '#F4F7F5', border: 'none', borderRadius: 999, padding: '11px 20px', fontSize: 14, fontFamily: "'Baloo 2', sans-serif", fontWeight: 700, cursor: 'pointer' };

export default function BusinessProfile() {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ businessName: '', businessAddress: '', businessCity: '', businessPostalCode: '', businessCountry: 'Argentina', businessPhone: '', instagramUrl: '', facebookUrl: '', businessDescription: '' });
  const [hours, setHours] = useState(defaultHours());
  const [saved, setSaved] = useState(false);
  const [geocoded, setGeocoded] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const init = async () => {
      if (!user?.email) return;
      try {
        const data = await bootstrapUser(user.email, user.displayName);
        setMe(data);
        setForm({
          businessName: data.businessName || '',
          businessAddress: data.businessAddress || '',
          businessCity: data.businessCity || '',
          businessPostalCode: data.businessPostalCode || '',
          businessCountry: data.businessCountry || 'Argentina',
          businessPhone: data.businessPhone || '',
          instagramUrl: data.instagramUrl || '',
          facebookUrl: data.facebookUrl || '',
          businessDescription: data.businessDescription || ''
        });
        if (data.businessHours && Object.keys(data.businessHours).length > 0) {
          setHours({ ...defaultHours(), ...data.businessHours });
        }
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user]);

  const updateDayHours = (day, field, value) => {
    setHours({ ...hours, [day]: { ...hours[day], [field]: value } });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaved(false);
    setGeocoded(null);
    try {
      const res = await updateBusinessProfile({ ...form, businessHours: hours });
      setSaved(true);
      setGeocoded(!!res.geocoded);
    } catch (e) {
      setError(e.response?.data?.error || 'No pudimos guardar los datos.');
    }
  };

  const content = { marginLeft: isMobile ? 0 : 210, paddingTop: isMobile ? 56 : 0, padding: 28, minHeight: '100vh', background: '#0A0F0D', fontFamily: "'Nunito', sans-serif" };

  if (loading) {
    return (
      <>
        <Sidebar active="negocio-perfil" />
        <div style={content}><p style={{ color: '#9CA8A2' }}>Cargando...</p></div>
      </>
    );
  }

  if (me?.role !== 'negocio' && me?.role !== 'superadmin') {
    return (
      <>
        <Sidebar active="negocio-perfil" />
        <div style={content}>
          <div style={card}>
            <h3 style={{ color: '#F4F7F5', fontFamily: "'Baloo 2', sans-serif" }}>Esta sección es solo para cuentas Negocio</h3>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Sidebar active="negocio-perfil" />
      <div style={content}>
        <h1 style={{ color: '#F4F7F5', fontSize: 28, fontWeight: 700, marginBottom: 4, fontFamily: "'Baloo 2', sans-serif" }}>Datos del negocio</h1>
        <p style={{ color: '#9CA8A2', fontSize: 13, marginBottom: 24 }}>Esta información se les muestra a las personas que se sumen a tus turnos.</p>

        <div style={card}>
          {error && <div style={{ background: 'rgba(239,68,68,0.14)', color: '#FF8A8A', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>{error}</div>}
          {saved && <div style={{ background: 'rgba(46,224,107,0.14)', color: '#5EE08A', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>✅ Guardado.</div>}

          <form onSubmit={handleSave}>
            <label style={label}>Nombre del negocio</label>
            <input style={input} placeholder="Club Floresta" value={form.businessName} onChange={e => setForm({ ...form, businessName: e.target.value })} />

            <label style={label}>Dirección (calle y número)</label>
            <input style={input} placeholder="Ramón Falcón 3406" value={form.businessAddress} onChange={e => setForm({ ...form, businessAddress: e.target.value })} />

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={label}>Ciudad</label>
                <input style={input} placeholder="Capital Federal" value={form.businessCity} onChange={e => setForm({ ...form, businessCity: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={label}>Código postal</label>
                <input style={input} placeholder="1408" value={form.businessPostalCode} onChange={e => setForm({ ...form, businessPostalCode: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={label}>País</label>
                <input style={input} placeholder="Argentina" value={form.businessCountry} onChange={e => setForm({ ...form, businessCountry: e.target.value })} />
              </div>
            </div>
            <p style={{ fontSize: 11.5, color: '#9CA8A2', marginTop: -8, marginBottom: 14 }}>
              Con la dirección completa calculamos tu ubicación real — así Winal puede decirle a la gente qué tan cerca está tu cancha.
            </p>

            {saved && geocoded === true && (
              <div style={{ background: 'rgba(46,224,107,0.14)', color: '#5EE08A', borderRadius: 10, padding: '10px 14px', fontSize: 12.5, marginBottom: 14 }}>📍 Ubicación confirmada — ya podemos calcular distancias.</div>
            )}
            {saved && geocoded === false && (
              <div style={{ background: 'rgba(255,200,87,0.12)', color: '#FFC857', borderRadius: 10, padding: '10px 14px', fontSize: 12.5, marginBottom: 14 }}>⚠️ No pudimos ubicar esa dirección en el mapa. Revisá que esté completa y bien escrita, y guardá de nuevo.</div>
            )}

            <label style={label}>Teléfono de contacto</label>
            <input style={input} placeholder="+54 9 11 ..." value={form.businessPhone} onChange={e => setForm({ ...form, businessPhone: e.target.value })} />

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={label}>Instagram</label>
                <input style={input} placeholder="instagram.com/tucancha" value={form.instagramUrl} onChange={e => setForm({ ...form, instagramUrl: e.target.value })} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={label}>Facebook</label>
                <input style={input} placeholder="facebook.com/tucancha" value={form.facebookUrl} onChange={e => setForm({ ...form, facebookUrl: e.target.value })} />
              </div>
            </div>

            <label style={label}>Descripción (opcional)</label>
            <textarea style={{ ...input, resize: 'none' }} rows={3} placeholder="Contales algo a quienes se sumen a tus turnos" value={form.businessDescription} onChange={e => setForm({ ...form, businessDescription: e.target.value })} />

            <label style={label}>Horarios de atención</label>
            <p style={{ fontSize: 11.5, color: '#9CA8A2', marginTop: -8, marginBottom: 10 }}>
              Es solo informativo — no impide que se reserven turnos fuera de este horario.
            </p>
            <div style={{ marginBottom: 16 }}>
              {DAYS_ES.map(day => (
                <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ width: 90, fontSize: 12.5, color: '#F4F7F5', fontWeight: 600 }}>{day}</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11.5, color: '#9CA8A2' }}>
                    <input type="checkbox" checked={hours[day]?.closed || false} onChange={e => updateDayHours(day, 'closed', e.target.checked)} />
                    Cerrado
                  </label>
                  {!hours[day]?.closed && (
                    <>
                      <input type="time" style={{ ...input, marginBottom: 0, width: 100, padding: '6px 8px' }} value={hours[day]?.open || '09:00'} onChange={e => updateDayHours(day, 'open', e.target.value)} />
                      <span style={{ color: '#9CA8A2', fontSize: 12 }}>a</span>
                      <input type="time" style={{ ...input, marginBottom: 0, width: 100, padding: '6px 8px' }} value={hours[day]?.close || '23:00'} onChange={e => updateDayHours(day, 'close', e.target.value)} />
                    </>
                  )}
                </div>
              ))}
            </div>

            <button className="winal-btn" style={btnPrimary} type="submit">Guardar datos</button>
          </form>
        </div>
      </div>
    </>
  );
}